import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { Button } from 'reactstrap';
import Body from '../Components/layout/Body';

const VISUAL_INTRO_HELP = `
  <div>
    <p><strong>Purpose: </strong>This section helps you screen for vision issues that may affect access.  
    This includes background info and a checklist of key vision issues.
    </p>
    <p><strong>Note: </strong>This screening info is basic and does not replace a formal vision assessment. 
    In some cases, referrals to professionals with specific training in visual challenges may be needed (including 
      certified low vision therapists, teachers of students with vision impairment, or neuro-optometrists).</p>
    <p><strong>Resources: </strong>For more about vision issues, see the following:
      <ul>
        <li><a href="https://www.pattan.net/Publications/Matching-AT-Features-to-Low-Vision-Needs" target="_blank">Matching Assistive Technology Features to Low Vision Needs</a> - a free download from PaTTAN</li>
        <li><a href="https://www.myeyedr.com/eye-health/vision-simulator" target="_blank">Vision Simulator</a> - simulates how different conditions impact vision</li>
        <li><a href="https://www.perkins.org/our-work/cvi/" target="_blank">CVI</a> - portal for information about cortical visual impairment</li>
      </ul>
    </p>

  </div>
`

const propTypes = {
  firstName: PropTypes.string,
  nextSection: PropTypes.string.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their']
};

//content sub-component with unique content for this page
function EvalAbilitiesVisualIntroContent(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const nextSection = props.nextSection;
  let history = useHistory();
  const curURL = new URL(window.location.href);

  return(
    <>
    <p>Learning more about {firstName}'s vision can help you:</p>
    <ul>
      <li>Ensure that {firstName} has {pronouns[2]} glasses or other visual supports needed for success</li>
      <li>Get a sense of whether there are visual issues that may affect access or require further evaluation</li>
      <li>Present materials in a way that {firstName} can see them</li>
      <li>Adjust access methods such as eye tracking to accommodate specific needs</li>
    </ul>
    <p>We recommend that everyone complete the vision information, since there may be vision issues that are not obvious to the naked eye :). But you may want to skip the vision piece if {firstName} is fully blind or other situations.</p>
    <p>
      <Button outline color="secondary"
        onClick={() => {    
          history.push({
            pathname: nextSection,
            search: curURL.search
          });
        }}>
        Skip the vision section
      </Button>
    </p>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function EvaluateAbilitiesVisualIntro(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const nextSection = props.nextSection
  const onContinue = props.onContinue;

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-eye",
    pageSymbolClassName: "aa-section-symbol eval",
    title: `Let's take a basic look at ${firstName}'s visual abilities`,
    subTitle: "The next couple of screens will help you gather preliminary information about vision capabilities.",
    learnMore: {linkText: "Learn More about basic visual screening",
                title: "Basic Visual Screening",
                content: VISUAL_INTRO_HELP
                },
    nextRoute: "", //this is governed by onContinue
    sidebar: false
  }

  console.log("Rendering EvaluateAbilitiesVisualIntro, nextSection: " + nextSection);
  
  return(
    <>
    <Body page={pageData} 
      contentComponent={<EvalAbilitiesVisualIntroContent firstName={firstName} pronouns={pronouns} nextSection={nextSection}/>}
      onContinue={onContinue} 
    />
    </>
  );
}

EvaluateAbilitiesVisualIntro.propTypes = propTypes;
EvaluateAbilitiesVisualIntro.defaultProps = defaultProps;

export default EvaluateAbilitiesVisualIntro;